/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useState } from "react";
import { Company, Effect, Nullable, ServiceLocation, WorkOrder, WorkOrderTask } from "../../utils/Interfaces";
import { NavigateFunction, useNavigate } from "react-router-dom";
import ConfirmDeleteModal from "../../components/confirmation/ConfirmDeleteModal";
import { deleteWorkOrder, editWorkOrderHeader } from "./workOrderFunctions";
import {
    convertHeaderDateTimes,
    formatDateWorkOrderHeader,
    getGoogleMapsURL,
    handleS3PDFDownload,
    mapWorkOrderStatusString,
    S3PDFRoute,
    sanitizeFileName,
    toUTC,
} from "../../utils/Functions";

import {
    Box,
    Card,
    CardActions,
    CardContent,
    Checkbox,
    Chip,
    Collapse,
    createStyles,
    Divider,
    Grid,
    IconButton,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";

import { makeStyles } from "@mui/styles";

import LoopIcon from "@mui/icons-material/Loop";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import FlagIcon from "@mui/icons-material/Flag";
import DirectionsIcon from "@mui/icons-material/Directions";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

import ExpandMore from "../../components/general/ExpandMore";
import InfoModal from "../../components/modal/InfoModal";

import { AnyAction, Dispatch } from "@reduxjs/toolkit";
import { selectRoles } from "../auth/authSlice";
import MultilineEditableField from "../../components/form-controls/MultilineEditableField";
import { Cancel, CheckCircle, EditLocation } from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { useWindowDimensions } from "../../utils/Hooks";
import WorkOrderTasks from "./WorkOrderTasks";

const useStyles = makeStyles(() =>
    createStyles({
        rotateIcon: {
            animation: "$spin 1s linear infinite",
        },
        "@keyframes spin": {
            "0%": {
                transform: "rotate(360deg)",
            },
            "100%": {
                transform: "rotate(0deg)",
            },
        },
    })
);

export interface WorkOrderHeaderProps {
    workOrder: WorkOrder;
    tasks: WorkOrderTask[];
}

const WorkOrderHeader = (props: WorkOrderHeaderProps) => {
    const { workOrder, tasks } = props;

    const { width } = useWindowDimensions();

    const site: ServiceLocation = workOrder.site;
    const company: Company = site.company;

    const nav: NavigateFunction = useNavigate();
    const classes = useStyles();

    const dispatch: Dispatch<AnyAction> = useAppDispatch();
    const navigate: NavigateFunction = useNavigate();

    const readOnly: boolean = !useAppSelector(selectRoles).includes("office");

    const [editing, setEditing] = useState<boolean>(false);
    const [ticketNo, setTicketNo] = useState<string>(workOrder.ticket_no);
    const [dateReceived, setDateReceived] = useState<Nullable<string>>(workOrder.date_received);
    const [dateCreated, setDateCreated] = useState<Nullable<string>>(workOrder.date_created);
    const [deviceToBeFixed, setDeviceToBeFixed] = useState<string>(workOrder.device_to_be_fixed);
    const [scissorLiftAvailable, setScissorLiftAvailable] = useState<boolean>(workOrder.scissor_lift_available);

    const [printRequest, setPrintRequest] = useState<boolean>(false);

    const [showDetails, setShowDetails] = useState<boolean>(false);

    const [confirmWorkOrderDeleteModalOpen, setConfirmWorkOrderDeleteModalOpen] = useState<boolean>(false);

    const [siteInfoModalOpen, setSiteInfoModalOpen] = useState<boolean>(false);

    const handleClose = () => {
        setConfirmWorkOrderDeleteModalOpen(false);
    };

    function restoreWorkOrderDefaults() {
        setDateReceived(workOrder.date_received);
        setDateCreated(workOrder.date_created);
        setDeviceToBeFixed(workOrder.device_to_be_fixed);
        setScissorLiftAvailable(workOrder.scissor_lift_available);
        setTicketNo(workOrder.ticket_no);
    }

    const onCancel = () => {
        restoreWorkOrderDefaults();
        setEditing(false);
    };

    const isDarkTheme: boolean = localStorage.getItem("techwiseTheme") === "dark";

    // @ts-ignore
    return (
        <Card sx={{ minWidth: 275 }}>
            <ConfirmDeleteModal
                onDelete={(modalCallback: Effect) => {
                    deleteWorkOrder(workOrder.id, dispatch, () => {
                        modalCallback();
                        navigate("/work-orders");
                    });
                }}
                confirmText="delete work order"
                open={confirmWorkOrderDeleteModalOpen}
                handleClose={handleClose}
                description="You are about to delete the following work order, including all contained technician forms, notes, assignees and file attachments."
            />

            <CardContent>
                {site && (
                    <InfoModal
                        title="Site Notes/Comments"
                        description={site.comments ?? "--"}
                        open={siteInfoModalOpen}
                        handleClose={() => setSiteInfoModalOpen(false)}
                    />
                )}

                <Grid container direction="row" justifyContent="space-between" alignItems="center">
                    <Box
                        style={{
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "space-between",
                        }}
                    >
                        {editing && <Typography sx={{ mt: 2.5, mr: 2, mb: 1.5 }}>Ticket &#35;</Typography>}

                        {editing && (
                            <TextField
                                margin="normal"
                                id="ticket_no"
                                name="ticket_no"
                                label="Ticket #"
                                value={ticketNo}
                                size="small"
                                onChange={(event) => setTicketNo(event.target.value)}
                            />
                        )}

                        {!editing && (
                            <Box>{ticketNo !== null && <Typography variant="h5">{`Ticket #${ticketNo}`}</Typography>}</Box>
                        )}
                    </Box>

                    {editing && (
                        <Box>
                            <Tooltip title="Cancel Changes">
                                <IconButton onClick={onCancel}>
                                    <Cancel />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Save Changes">
                                <IconButton
                                    color="primary"
                                    onClick={() => {
                                        editWorkOrderHeader(
                                            workOrder.id,
                                            ticketNo,
                                            dateReceived === null ? null : toUTC(new Date(dateReceived)),
                                            dateCreated === null ? null : toUTC(new Date(dateCreated)),
                                            deviceToBeFixed,
                                            scissorLiftAvailable,
                                            workOrder.comments,
                                            dispatch,
                                            () => {}
                                        );
                                        setEditing(false);
                                    }}
                                >
                                    <CheckCircle />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}
                    {!editing && (
                        <Box>
                            {site.id !== null && (
                                <Box>
                                    {printRequest && (
                                        <Tooltip title={"Processing Print Request..."}>
                                            <IconButton disabled size="large">
                                                {/* @ts-ignore */}
                                                <LoopIcon className={classes.rotateIcon} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    {!printRequest && (
                                        <Tooltip title={`Generate Consolidated PDF for Work Order #${ticketNo}.`}>
                                            <IconButton
                                                onClick={() => {
                                                    handleS3PDFDownload(
                                                        S3PDFRoute.WorkOrder,
                                                        workOrder.id,
                                                        dispatch,
                                                        setPrintRequest,
                                                        sanitizeFileName(`${ticketNo}.pdf`)
                                                    );
                                                }}
                                                disabled={readOnly}
                                                size="large"
                                            >
                                                <PictureAsPdfIcon />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    <Tooltip title={`Edit Ticket #${ticketNo} Details`}>
                                        <IconButton onClick={() => setEditing(true)} disabled={readOnly} size="large">
                                            <EditIcon />
                                        </IconButton>
                                    </Tooltip>

                                    <Tooltip title={`Delete Ticket #${ticketNo}`}>
                                        <IconButton
                                            onClick={() => {
                                                setConfirmWorkOrderDeleteModalOpen(true);
                                            }}
                                            size="large"
                                            disabled={readOnly}
                                        >
                                            <DeleteIcon color={readOnly ? "secondary" : "error"} />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            )}
                        </Box>
                    )}
                </Grid>

                <Box sx={{ mb: 3 }} />

                <Grid container direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography>Date Created</Typography>

                    {editing && (
                        <Grid item xs={6}>
                            <TextField
                                id="date_created"
                                label="Date Created"
                                type="datetime-local"
                                value={convertHeaderDateTimes(dateCreated as string | Date)}
                                onChange={(event) => {
                                    setDateCreated(event.target.value + ":00");
                                }}
                                size="small"
                                fullWidth
                                inputProps={{
                                    step: 60,
                                }}
                                sx={{ colorScheme: isDarkTheme ? "dark" : "light" }}
                            />
                        </Grid>
                    )}

                    {!editing && (
                        <Box>
                            {site.id !== null && (
                                <Typography>{`${dateCreated === null ? "--" : formatDateWorkOrderHeader(dateCreated)}`}</Typography>
                            )}
                        </Box>
                    )}
                </Grid>

                {editing && <Box sx={{ marginBottom: 2 }} />}

                <Grid container direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography>Date Received</Typography>

                    {editing && (
                        <Grid item xs={6}>
                            <TextField
                                id="date_received"
                                label="Date Received"
                                type="datetime-local"
                                value={convertHeaderDateTimes(dateReceived as string | Date)}
                                onChange={(event) => {
                                    setDateReceived(event.target.value + ":00");
                                }}
                                size="small"
                                fullWidth
                                inputProps={{
                                    step: 60,
                                }}
                                sx={{ colorScheme: isDarkTheme ? "dark" : "light" }}
                            />
                        </Grid>
                    )}

                    {!editing && (
                        <Box>
                            {site.id !== null && (
                                <Typography>{`${dateReceived === null ? "--" : formatDateWorkOrderHeader(dateReceived)}`}</Typography>
                            )}
                        </Box>
                    )}
                </Grid>

                <Grid container direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography>Device to be Fixed</Typography>

                    {editing && (
                        <Grid item xs={6}>
                            <TextField
                                margin="normal"
                                name="device_to_be_fixed"
                                label="Device to be Fixed"
                                type="device_to_be_fixed"
                                id="device_to_be_fixed"
                                value={deviceToBeFixed}
                                size="small"
                                fullWidth
                                onChange={(event) => setDeviceToBeFixed(event.target.value)}
                            />
                        </Grid>
                    )}
                    {!editing && (
                        <Box>
                            {site.id !== null && (
                                <Typography>{`${deviceToBeFixed === null ? "--" : deviceToBeFixed}`}</Typography>
                            )}
                        </Box>
                    )}
                </Grid>

                <Grid container direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography>Scissor Lift Available</Typography>

                    {editing && (
                        <Checkbox
                            id="scissor_lift_available"
                            name="scissor_lift_available"
                            onClick={() => setScissorLiftAvailable(!scissorLiftAvailable)}
                            checked={scissorLiftAvailable}
                        />
                    )}

                    {!editing && (
                        <Box>
                            {site.id !== null && (
                                <Typography component="span">
                                    {scissorLiftAvailable ? (
                                        <Chip label="Yes" color="success" style={{ fontWeight: "bold" }} />
                                    ) : (
                                        <Chip label="No" color="error" style={{ fontWeight: "bold" }} />
                                    )}
                                </Typography>
                            )}
                        </Box>
                    )}
                </Grid>

                <Grid container direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography>Number of Trips</Typography>

                    <Box>{site.id !== null && <Typography>{workOrder.trip_number}</Typography>}</Box>
                </Grid>

                <Grid container direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography>Work Order Status</Typography>

                    {site.id !== null && <Typography>{`${mapWorkOrderStatusString(workOrder.status)}`}</Typography>}
                </Grid>
            </CardContent>

            <Divider variant="middle" sx={{ mt: 1 }}>
                <Typography variant="h6">Service Location</Typography>
            </Divider>

            <CardContent>
                <Grid
                    container
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                    }}
                >
                    <Box>
                        {site.store_no !== null && company.name !== null && (
                            <Typography
                                display="flex"
                                flexDirection="row"
                                alignItems="center"
                                color="text.secondary"
                                component="span"
                            >
                                {`${company.name} Store`}&nbsp;
                                <Typography fontWeight="bold">#{site.store_no}</Typography>
                            </Typography>
                        )}

                        {site.street_address !== null && (
                            <Typography color="text.secondary" fontWeight="bold">
                                {`${site.street_address}`}
                            </Typography>
                        )}

                        {site.city !== null && site.state !== null && (
                            <Typography color="text.secondary" fontWeight="bold">
                                {`${site.city}, ${site.state} ${site.zip_code ? site.zip_code : ""}`}
                            </Typography>
                        )}
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
                        {site.id !== null && (
                            <Stack direction="row">
                                <Tooltip title="View or edit service location">
                                    <Stack direction="column" justifyContent="center">
                                        <IconButton onClick={() => nav(`/work-sites/${site.id}`)} color="info">
                                            <EditLocation />
                                        </IconButton>
                                    </Stack>
                                </Tooltip>
                                <Tooltip title="Get Directions using Google Maps">
                                    <Stack direction="column" justifyContent="center">
                                        <IconButton color="info" onClick={() => window.open(getGoogleMapsURL(site), "_blank")}>
                                            <DirectionsIcon />
                                        </IconButton>
                                    </Stack>
                                </Tooltip>
                                {site.comments !== "" && site.comments !== null && (
                                    <Tooltip title="View Site Notes/Comments">
                                        <Stack direction="column" justifyContent="center">
                                            <IconButton color="info" onClick={() => setSiteInfoModalOpen(true)}>
                                                <FlagIcon />
                                            </IconButton>
                                        </Stack>
                                    </Tooltip>
                                )}
                            </Stack>
                        )}
                    </Box>
                </Grid>
            </CardContent>

            <Divider variant="middle">
                <Stack direction="row" justifyContent="space-between">
                    <Stack direction="column" justifyContent="center">
                        <Typography variant="h6">Work Order Details</Typography>
                    </Stack>
                    <Stack direction="column" justifyContent="center">
                        <CardActions disableSpacing>
                            {/* @ts-ignore */}
                            <ExpandMore
                                expand={showDetails}
                                onClick={() => setShowDetails(!showDetails)}
                                aria-expanded={showDetails}
                                aria-label="show more"
                            >
                                <Tooltip title={showDetails ? "Hide Ticket Details" : "Show Ticket Details"}>
                                    <ExpandMoreIcon fontSize={width < 800 ? "small" : "medium"} />
                                </Tooltip>
                            </ExpandMore>
                        </CardActions>
                    </Stack>
                </Stack>
            </Divider>

            <CardContent sx={{ pt: 0, pb: 0, mt: 2 }}>
                {!showDetails && (
                    <Box>
                        <Typography>{workOrder.problem_description}</Typography>
                        <Typography>...</Typography>
                    </Box>
                )}

                {showDetails && (
                    <Collapse in={showDetails} timeout="auto" unmountOnExit>
                        <MultilineEditableField<WorkOrder>
                            title="Problem Description"
                            lines={4}
                            body={workOrder.problem_description}
                            updateKey="problem_description"
                            updatePath={`/api/work-orders/update/${workOrder.id}`}
                            editable={!readOnly}
                        />

                        <MultilineEditableField<WorkOrder>
                            title="Additional Description"
                            lines={4}
                            body={workOrder.additional_description}
                            updateKey="additional_description"
                            updatePath={`/api/work-orders/update/${workOrder.id}`}
                            editable={!readOnly}
                        />

                        {!readOnly && (
                            <MultilineEditableField<WorkOrder>
                                title="Work Order Comments"
                                lines={4}
                                body={workOrder.comments ?? ""}
                                updateKey="comments"
                                updatePath={`/api/work-orders/update/${workOrder.id}`}
                                editable={!readOnly}
                            />
                        )}
                    </Collapse>
                )}
            </CardContent>

            <Divider variant="middle">
                <Typography variant="h6">Work Order Punchlist</Typography>
            </Divider>

            <WorkOrderTasks workOrderId={workOrder.id} tasks={tasks} allowTaskEdit />

            <Divider variant="middle">
                <Typography variant="h6">Work Order Attachments</Typography>
            </Divider>

            <CardContent>
                <Typography textAlign="center" color="text.secondary">
                    Work in Progress...
                </Typography>
            </CardContent>
        </Card>
    );
};

export default WorkOrderHeader;
